import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  PUSH_EXAMPLES,
  PUSH_STACK_SIZE,
} from '@/pages/auth/auth-push-examples';
import { NOTIFICATION_ENTRANCE_TRANSITION } from '@/pages/onboarding/mockup/notification-motion';

const PUSH_INTERVAL_MS = 3200;
/** Minutes shown for the 2nd and 3rd banner ("há 2 min", "há 5 min"). */
const OLDER_MINUTES = [2, 5];

const mod = (n: number, m: number) => ((n % m) + m) % m;

/**
 * Lock-screen notifications landing one after another, newest on top, the
 * oldest dropping off the bottom. Same banner look and entrance spring as
 * the onboarding's NotificationBanner. Holds still under reduced motion.
 */
export function AuthPushStack() {
  const { LL } = useI18nContext();
  const shouldReduceMotion = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion) return;
    const id = window.setInterval(
      () => setTick((t) => t + 1),
      PUSH_INTERVAL_MS
    );
    return () => window.clearInterval(id);
  }, [shouldReduceMotion]);

  const visible = Array.from({ length: PUSH_STACK_SIZE }, (_, age) => ({
    id: tick - age,
    push: PUSH_EXAMPLES[mod(tick - age, PUSH_EXAMPLES.length)],
    when:
      age === 0
        ? LL.auth.pushNow()
        : LL.auth.pushMinutesAgo({ minutes: OLDER_MINUTES[age - 1] }),
  }));

  return (
    <ul className="flex flex-col gap-2 px-3 pb-4" data-testid="auth-push-stack">
      <AnimatePresence initial={false} mode="popLayout">
        {visible.map(({ id, push, when }) => (
          <motion.li
            key={id}
            layout={!shouldReduceMotion}
            initial={{ opacity: 0, y: -18, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={NOTIFICATION_ENTRANCE_TRANSITION}
            className="flex items-start gap-2.5 rounded-2xl bg-white/90 p-3 shadow-lg backdrop-blur"
          >
            <img
              src={push.icon}
              alt=""
              className="size-8 shrink-0 rounded-[22%] object-cover"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-[13px] font-semibold text-black">
                  {push.title}
                </span>
                <span className="shrink-0 text-[11px] text-[#5F6368]">
                  {when}
                </span>
              </div>
              <p className="line-clamp-2 text-[12px] text-black/80">
                {push.body}
              </p>
            </div>
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  );
}
